import { useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

/**
 * Canonical Profile hook — SINGLE SOURCE OF TRUTH for the signed-in user's
 * profile. Backed by React Query so all consumers share one cached row and
 * one in-flight request. `useProfileOptimized` is a thin re-export of this
 * hook kept for backward compatibility with existing imports.
 *
 * For fetching a DIFFERENT user's public profile by user_id, use
 * `useUserProfile(userId)` instead — that hook is intentionally separate.
 */
export interface Profile {
  id: string;
  user_id: string;
  email: string;
  display_name: string;
  avatar_url: string | null;
  phone: string | null;
  role: 'customer' | 'organizer' | 'admin';
  organizer_business_name: string | null;
  organizer_logo_url: string | null;
  organizer_address: string | null;
  organizer_bio: string | null;
  organizer_website: string | null;
  organizer_verified: boolean;
  organizer_setup_completed: boolean;
  stripe_connect_account_id: string | null;
  stripe_connect_onboarding_complete: boolean;
  stripe_connect_charges_enabled: boolean;
  stripe_connect_payouts_enabled: boolean;
  stripe_connect_created_at: string | null;
  created_at: string;
  updated_at: string;
}

const PROFILE_QUERY_KEY = ['profile', 'self'] as const;

const fetchOwnProfile = async (userId: string): Promise<Profile | null> => {
  // Sensitive columns (phone, stripe_connect_account_id, billing_*) are
  // column-locked via REVOKE. Owner reads them via get_my_profile_private
  // SECURITY DEFINER RPC and we merge them into the returned profile.
  const { data, error } = await supabase
    .from('profiles')
    .select(`
      id, user_id, email, display_name, avatar_url, role,
      organizer_business_name, organizer_logo_url, organizer_address,
      organizer_bio, organizer_website,
      organizer_verified, organizer_setup_completed,
      stripe_connect_onboarding_complete, stripe_connect_charges_enabled,
      stripe_connect_payouts_enabled, stripe_connect_created_at,
      created_at, updated_at
    `)
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  let phone: string | null = null;
  let stripe_connect_account_id: string | null = null;
  try {
    const { data: priv } = await supabase.rpc('get_my_profile_private' as any);
    const row = Array.isArray(priv) ? priv[0] : null;
    phone = (row?.phone as string | null) ?? null;
    stripe_connect_account_id = (row?.stripe_connect_account_id as string | null) ?? null;
  } catch { /* tolerate */ }

  return { ...(data as any), phone, stripe_connect_account_id } as Profile;
};

export const useProfile = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const profileQuery = useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: () => fetchOwnProfile(user!.id),
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Invalidate cache on auth state changes (sign-in / sign-out).
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
        queryClient.invalidateQueries({ queryKey: ['profile'] });
      }
    });
    return () => subscription.unsubscribe();
  }, [queryClient]);

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Profile>) => {
      if (!user) throw new Error('Not authenticated');
      // Strip role from updates - role changes must go through secure RPCs.
      const { role, ...safeUpdates } = updates as any;
      const { error } = await supabase
        .from('profiles')
        .update({ ...safeUpdates, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);
      if (error) throw error;
      return safeUpdates as Partial<Profile>;
    },
    onSuccess: (applied) => {
      queryClient.setQueryData<Profile | null>(PROFILE_QUERY_KEY, (prev) =>
        prev ? { ...prev, ...applied } : prev
      );
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast({
        title: 'Profile updated',
        description: 'Your profile has been successfully updated.',
      });
    },
  });

  const updateProfile = useCallback(
    async (updates: Partial<Profile>) => {
      try {
        await updateMutation.mutateAsync(updates);
        return { error: null };
      } catch (err: any) {
        return { error: err?.message ?? 'Update failed' };
      }
    },
    [updateMutation]
  );

  const becomeOrganizer = useCallback(
    async (data: {
      businessName: string;
      phone: string;
      logoUrl?: string;
      address?: string;
      bankDetails?: any;
    }) => {
      if (!user) return { error: 'Not authenticated' };
      try {
        const { error } = await supabase.rpc('become_organizer', {
          business_name: data.businessName,
          phone_number: data.phone,
          logo_url: data.logoUrl || null,
          bank_details: data.bankDetails || null,
          business_address: data.address || null,
        });
        if (error) throw error;
        await queryClient.invalidateQueries({ queryKey: ['profile'] });
        toast({
          title: 'Welcome to organizers!',
          description: 'You can now create and manage events.',
        });
        return { error: null };
      } catch (err: any) {
        return { error: err?.message ?? 'Failed to become organizer' };
      }
    },
    [user, queryClient, toast]
  );

  const refetchProfile = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['profile'] }),
    [queryClient]
  );

  const profile = useMemo(() => profileQuery.data ?? null, [profileQuery.data]);

  return {
    profile,
    loading: profileQuery.isLoading || (!!user && profileQuery.isFetching && !profileQuery.data),
    error: (profileQuery.error as Error | null)?.message ?? null,
    updateProfile,
    becomeOrganizer,
    refetchProfile,
  };
};
